<template>
  <div>
    <v-row>
      <v-col v-if="!isOrganizationManager" cols="12" sm="6" md="3" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="organization"
          :items="organizations"
          item-value="_id"
          outlined
          dense
          background-color="white"
          :label="$t('user.registration.organization')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.name) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.name) }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="employee"
          :items="employees"
          item-value="_id"
          item-text="name"
          outlined
          dense
          background-color="white"
          :label="$t('reports.employee')"
          class="rounded-lg"
          clearable
        >
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ item.name }}
              {{ item.email ? `(${item.email})` : "" }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="quizContent"
          :items="quizContents"
          item-value="_id"
          outlined
          dense
          background-color="white"
          :label="$t('quiz.assessment')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.title) }}
          </template>

          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.title) }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="loading"
              hide-details
              v-model="formattedStartDate"
              :label="$t('reports.chooseFromDate')"
              class="rounded-lg"
              outlined
              dense
              background-color="white"
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
            >
              <v-icon slot="append" color="textLight">
                mdi-calendar-blank-outline
              </v-icon></v-text-field
            >
          </template>
          <v-date-picker
            color="primary"
            v-model="startDate"
            no-title
            @input="startDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="loading"
              hide-details
              class="rounded-lg"
              :label="$t('reports.chooseToDate')"
              v-model="formattedEndDate"
              outlined
              dense
              background-color="white"
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
            >
              <v-icon slot="append" color="textLight">
                mdi-calendar-blank-outline
              </v-icon></v-text-field
            >
          </template>

          <v-date-picker
            color="primary"
            v-model="endDate"
            no-title
            @input="endDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "AssessmentFilters",
  data() {
    return {
      organization: null,
      organizations: [],
      employeeList: [],
      employee: null,
      quizContents: null,
      quizContent: null,
      startDate: null,
      startDateMenu: false,
      endDate: null,
      endDateMenu: false,
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isOrganizationManager() {
      return (
        this.$store.state.authenticatedUser?.isOrganizationManager ?? false
      );
    },
    formattedStartDate: {
      get() {
        return this.startDate
          ? this.$moment(this.startDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.startDate = value;
      },
    },
    formattedEndDate: {
      get() {
        return this.endDate
          ? this.$moment(this.endDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.endDate = value;
      },
    },
    employees() {
      return this.employeeList.reduce((result, employee) => {
        const obj = {
          _id: employee._id,
          name: `${employee?.user?.firstName || ""} ${
            employee?.user?.lastName || ""
          }`,
          email: employee?.user?.email,
        };

        if (this.organization) {
          if (employee.organization?._id === this.organization) {
            result.push(obj);
          }
        } else {
          result.push(obj);
        }

        return result;
      }, []);
    },
  },
  watch: {
    startDate() {
      if (this.startDate && this.endDate) {
        this.$emit("filter", "dates", {
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } else if (!this.startDate || !this.endDate) {
        this.$emit("filter", "dates", {
          startDate: null,
          endDate: null,
        });
      }
    },
    endDate() {
      if (this.startDate && this.endDate) {
        this.$emit("filter", "dates", {
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } else if (!this.startDate || !this.endDate) {
        this.$emit("filter", "dates", {
          startDate: null,
          endDate: null,
        });
      }
    },
    organization() {
      this.$emit("filter", "organization", this.organization);
      if (this.organization) this.employee = null;
    },
    employee() {
      this.$emit("filter", "employee", this.employee);
    },
    quizContent() {
      this.$emit("filter", "quizContent", this.quizContent);
    },
  },
  apollo: {
    getOrganizations() {
      return {
        query: require("../../organization-module/api/query/getOrganizations.gql"),
        variables: { limit: 0, skip: 0, filterInput: null },
        result({ data, loading }) {
          if (
            data &&
            data.getOrganizations &&
            data.getOrganizations.organizations
          ) {
            this.organizations = data.getOrganizations.organizations;
          }
        },
      };
    },
    getQuizContents() {
      return {
        query: require("../../quiz-module/api/query/getQuizContents.gql"),
        variables: {
          pageInput: this.defaultPageInput(0),
          filterInput: { approvalStatus: "APPROVED", isActive: true },
        },
        result({ data, loading }) {
          if (data?.getQuizContents?.quizContents) {
            this.quizContents = data.getQuizContents.quizContents;
          }
        },
      };
    },
    getEmployees() {
      return {
        query: require("../../user-module/api/query/getEmployees.gql"),
        variables: {
          limit: 0,
          skip: 0,
        },
        result({ data, loading }) {
          if (data?.getEmployees) {
            this.employeeList = data.getEmployees.employees;
          }
        },
      };
    },
  },
};
</script>
